import React, { useState, useCallback } from 'react';
import Icon from '../../components/AppIcon';

const ACCENT = '#D6EF4E';

const ROUNDINGS = [
  { label: '0,90 €', value: 0.90 },
  { label: '0,95 €', value: 0.95 },
  { label: '0,99 €', value: 0.99 },
  { label: 'Manual', value: null },
];

const DEPARTMENTS = ['Tienda Física', 'Tienda Online', 'Sala de Corte', 'Distribución', 'Corporativo'];

const defaultCosts = {
  compra: '',
  transporte: '',
  costes_adicionales: '',
  coste_transformacion: '',
  coste_loncheado: '',
  merma_pct: '',
};

const defaultMargins = {
  minimo: '25',
  objetivo: '35',
  premium: '50',
};

function applyRounding(price, rounding) {
  if (rounding === null) return price;
  const base = Math.floor(price);
  return base + rounding;
}

function calcPVP(coste, margen) {
  if (!coste || !margen || margen >= 100) return 0;
  return coste / (1 - margen / 100);
}

export default function CostesPricing() {
  const [costs, setCosts] = useState(defaultCosts);
  const [margins, setMargins] = useState(defaultMargins);
  const [rounding, setRounding] = useState(0.99);
  const [customRounding, setCustomRounding] = useState('');
  const [productName, setProductName] = useState('');
  const [department, setDepartment] = useState('');
  const [activeTab, setActiveTab] = useState('calculadora');

  const updateCost = (field, value) => setCosts(prev => ({ ...prev, [field]: value }));
  const updateMargin = (field, value) => setMargins(prev => ({ ...prev, [field]: value }));

  const costeReal = useCallback(() => {
    const c = parseFloat(costs?.compra) || 0;
    const t = parseFloat(costs?.transporte) || 0;
    const a = parseFloat(costs?.costes_adicionales) || 0;
    const tr = parseFloat(costs?.coste_transformacion) || 0;
    const l = parseFloat(costs?.coste_loncheado) || 0;
    const merma = parseFloat(costs?.merma_pct) || 0;
    const base = c + t + a + tr + l;
    return base * (1 + merma / 100);
  }, [costs]);

  const coste = costeReal();
  const pvpMin = calcPVP(coste, parseFloat(margins?.minimo));
  const pvpObj = calcPVP(coste, parseFloat(margins?.objetivo));
  const pvpPremium = calcPVP(coste, parseFloat(margins?.premium));

  const effectiveRounding = rounding !== null ? rounding : (parseFloat(customRounding) || 0);
  const pvpMinR = coste > 0 ? applyRounding(pvpMin, effectiveRounding) : 0;
  const pvpObjR = coste > 0 ? applyRounding(pvpObj, effectiveRounding) : 0;
  const pvpPremiumR = coste > 0 ? applyRounding(pvpPremium, effectiveRounding) : 0;

  const fmt = (n) => n > 0 ? n?.toFixed(2) + ' €' : '—';
  const fmtPct = (pvp, c) => pvp > 0 && c > 0 ? (((pvp - c) / pvp) * 100)?.toFixed(1) + '%' : '—';

  return (
    <div className="min-h-screen px-4 py-6 md:px-8" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-5 font-oranienbaum text-white/30" style={{ fontSize: 'var(--fs-sm)' }}>
        <Icon name="Home" size={13} />
        <span>/</span>
        <span className="text-[#D6EF4E]/60">Inventario</span>
        <span>/</span>
        <span className="text-white/50">Costes y Pricing</span>
      </div>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(214,239,78,0.10)', border: '1px solid rgba(214,239,78,0.25)' }}>
            <Icon name="Calculator" size={22} className="text-[#D6EF4E]" />
          </div>
          <div>
            <h1 className="font-oranienbaum font-bold text-white tracking-wide" style={{ fontSize: 'var(--fs-page-title)' }}>Costes y Pricing</h1>
            <p className="font-oranienbaum text-white/40" style={{ fontSize: 'var(--fs-sm)' }}>Motor avanzado de precios y márgenes</p>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { id: 'calculadora', label: 'Calculadora de Costes', icon: 'Calculator' },
          { id: 'margenes', label: 'Configuración Márgenes', icon: 'Percent' },
          { id: 'lector', label: 'Lector de Precios', icon: 'Search' },
        ]?.map(tab => (
          <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-oranienbaum transition-all"
            style={{
              background: activeTab === tab?.id ? `${ACCENT}15` : 'rgba(255,255,255,0.04)',
              border: `1px solid ${activeTab === tab?.id ? `${ACCENT}35` : 'rgba(255,255,255,0.08)'}`,
              color: activeTab === tab?.id ? ACCENT : 'rgba(255,255,255,0.55)',
              fontSize: 'var(--fs-sm)',
              fontWeight: activeTab === tab?.id ? 600 : 400,
            }}>
            <Icon name={tab?.icon} size={14} />
            {tab?.label}
          </button>
        ))}
      </div>
      {/* Calculadora tab */}
      {activeTab === 'calculadora' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Cost inputs */}
          <div className="space-y-4">
            {/* Product info */}
            <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <h3 className="font-oranienbaum font-bold text-white mb-4" style={{ fontSize: 'var(--fs-section-title)' }}>Producto</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-oranienbaum text-white/40 text-xs block mb-1">Nombre del producto</label>
                  <input value={productName} onChange={e => setProductName(e?.target?.value)} placeholder="Ej: Jamón Ibérico 5J"
                    className="w-full font-oranienbaum text-sm rounded-xl px-3 py-2.5 border outline-none text-white placeholder-white/20"
                    style={{ background: '#0D0D0D', borderColor: 'rgba(255,255,255,0.12)', fontSize: 'var(--fs-base)' }} />
                </div>
                <div>
                  <label className="font-oranienbaum text-white/40 text-xs block mb-1">Departamento</label>
                  <select value={department} onChange={e => setDepartment(e?.target?.value)}
                    className="w-full font-oranienbaum rounded-xl px-3 py-2.5 border outline-none text-white"
                    style={{ background: '#0D0D0D', borderColor: 'rgba(255,255,255,0.12)', fontSize: 'var(--fs-base)' }}>
                    <option value="">Seleccionar...</option>
                    {DEPARTMENTS?.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Cost breakdown */}
            <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <h3 className="font-oranienbaum font-bold text-white mb-4" style={{ fontSize: 'var(--fs-section-title)' }}>
                Coste Real Final = Compra + Transporte + Adicionales + Transformación + Loncheado + Merma
              </h3>
              <div className="space-y-3">
                {[
                  { key: 'compra', label: 'Precio de Compra', placeholder: '0,00', suffix: '€', icon: 'ShoppingCart' },
                  { key: 'transporte', label: 'Transporte', placeholder: '0,00', suffix: '€', icon: 'Truck' },
                  { key: 'costes_adicionales', label: 'Costes Adicionales', placeholder: '0,00', suffix: '€', icon: 'Plus' },
                  { key: 'coste_transformacion', label: 'Coste Transformación', placeholder: '0,00', suffix: '€', icon: 'Settings' },
                  { key: 'coste_loncheado', label: 'Coste Loncheado', placeholder: '0,00', suffix: '€', icon: 'Scissors' },
                  { key: 'merma_pct', label: 'Merma', placeholder: '0', suffix: '%', icon: 'TrendingDown' },
                ]?.map(field => (
                  <div key={field?.key} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(214,239,78,0.08)', border: '1px solid rgba(214,239,78,0.15)' }}>
                      <Icon name={field?.icon} size={14} className="text-[#D6EF4E]/60" />
                    </div>
                    <label className="font-oranienbaum text-white/60 flex-1" style={{ fontSize: 'var(--fs-sm)' }}>{field?.label}</label>
                    <div className="flex items-center gap-1">
                      <input type="number" value={costs?.[field?.key]} onChange={e => updateCost(field?.key, e?.target?.value)}
                        placeholder={field?.placeholder}
                        className="w-24 font-oranienbaum text-right rounded-xl px-3 py-2 border outline-none text-white placeholder-white/20"
                        style={{ background: '#0D0D0D', borderColor: 'rgba(255,255,255,0.12)', fontSize: 'var(--fs-base)' }} />
                      <span className="font-oranienbaum text-white/40 w-5 text-center" style={{ fontSize: 'var(--fs-sm)' }}>{field?.suffix}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Total cost */}
              <div className="mt-4 pt-4 border-t flex items-center justify-between" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                <span className="font-oranienbaum font-bold text-white" style={{ fontSize: 'var(--fs-base)' }}>Coste Real Total</span>
                <span className="font-oranienbaum font-bold" style={{ color: ACCENT, fontSize: 'var(--fs-kpi-value)' }}>{fmt(coste)}</span>
              </div>
            </div>

            {/* Rounding */}
            <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <h3 className="font-oranienbaum font-bold text-white mb-3" style={{ fontSize: 'var(--fs-section-title)' }}>Redondeo de Precios</h3>
              <div className="flex flex-wrap gap-2">
                {ROUNDINGS?.map(r => (
                  <button key={r?.label} onClick={() => setRounding(r?.value)}
                    className="px-4 py-2 rounded-xl font-oranienbaum transition-all"
                    style={{
                      background: rounding === r?.value ? `${ACCENT}15` : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${rounding === r?.value ? `${ACCENT}35` : 'rgba(255,255,255,0.08)'}`,
                      color: rounding === r?.value ? ACCENT : 'rgba(255,255,255,0.55)',
                      fontSize: 'var(--fs-sm)',
                    }}>
                    {r?.label}
                  </button>
                ))}
                {rounding === null && (
                  <input type="number" value={customRounding} onChange={e => setCustomRounding(e?.target?.value)}
                    placeholder="0,00" step="0.01"
                    className="w-24 font-oranienbaum text-right rounded-xl px-3 py-2 border outline-none text-white placeholder-white/20"
                    style={{ background: '#0D0D0D', borderColor: `${ACCENT}40`, fontSize: 'var(--fs-base)' }} />
                )}
              </div>
            </div>
          </div>

          {/* Right: PVP results */}
          <div className="space-y-4">
            {/* Margin config */}
            <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <h3 className="font-oranienbaum font-bold text-white mb-4" style={{ fontSize: 'var(--fs-section-title)' }}>Configuración de Márgenes</h3>
              <div className="space-y-3">
                {[
                  { key: 'minimo', label: 'Margen Mínimo', color: '#F87171' },
                  { key: 'objetivo', label: 'Margen Objetivo', color: ACCENT },
                  { key: 'premium', label: 'Margen Premium', color: '#A78BFA' },
                ]?.map(m => (
                  <div key={m?.key} className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: m?.color }} />
                    <label className="font-oranienbaum text-white/60 flex-1" style={{ fontSize: 'var(--fs-sm)' }}>{m?.label}</label>
                    <div className="flex items-center gap-1">
                      <input type="number" value={margins?.[m?.key]} onChange={e => updateMargin(m?.key, e?.target?.value)}
                        min="0" max="99" step="1"
                        className="w-20 font-oranienbaum text-right rounded-xl px-3 py-2 border outline-none text-white"
                        style={{ background: '#0D0D0D', borderColor: `${m?.color}40`, color: m?.color, fontSize: 'var(--fs-base)' }} />
                      <span className="font-oranienbaum text-white/40 w-4" style={{ fontSize: 'var(--fs-sm)' }}>%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* PVP Results */}
            <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <h3 className="font-oranienbaum font-bold text-white mb-4" style={{ fontSize: 'var(--fs-section-title)' }}>
                PVP = Coste / (1 − Margen)
              </h3>
              <div className="space-y-3">
                {[
                  { label: 'Precio Mínimo Rentable', pvp: pvpMinR, margin: margins?.minimo, color: '#F87171', icon: 'ArrowDown' },
                  { label: 'Precio Recomendado', pvp: pvpObjR, margin: margins?.objetivo, color: ACCENT, icon: 'Target', highlight: true },
                  { label: 'Precio Premium', pvp: pvpPremiumR, margin: margins?.premium, color: '#A78BFA', icon: 'Star' },
                ]?.map((item, i) => (
                  <div key={i} className="rounded-xl p-4 flex items-center justify-between"
                    style={{ background: item?.highlight ? `${item?.color}10` : 'rgba(255,255,255,0.03)', border: `1px solid ${item?.highlight ? `${item?.color}30` : 'rgba(255,255,255,0.06)'}` }}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${item?.color}15` }}>
                        <Icon name={item?.icon} size={15} style={{ color: item?.color }} />
                      </div>
                      <div>
                        <p className="font-oranienbaum font-medium text-white" style={{ fontSize: 'var(--fs-sm)' }}>{item?.label}</p>
                        <p className="font-oranienbaum text-white/40" style={{ fontSize: 'var(--fs-xs)' }}>Margen: {item?.margin}%</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-oranienbaum font-bold" style={{ color: item?.color, fontSize: 'var(--fs-kpi-value)' }}>{fmt(item?.pvp)}</p>
                      <p className="font-oranienbaum text-white/40" style={{ fontSize: 'var(--fs-xs)' }}>Margen real: {fmtPct(item?.pvp, coste)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Summary */}
            {coste > 0 && (
              <div className="rounded-2xl p-5" style={{ background: `${ACCENT}08`, border: `1px solid ${ACCENT}20` }}>
                <h3 className="font-oranienbaum font-bold text-white mb-3" style={{ fontSize: 'var(--fs-section-title)' }}>Resumen</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Coste Real', value: fmt(coste), color: '#F87171' },
                    { label: 'PVP Recomendado', value: fmt(pvpObjR), color: ACCENT },
                    { label: 'Margen Objetivo', value: margins?.objetivo + '%', color: ACCENT },
                    { label: 'Beneficio/Unidad', value: fmt(pvpObjR - coste), color: '#34D399' },
                  ]?.map((item, i) => (
                    <div key={i} className="rounded-xl p-3" style={{ background: 'rgba(0,0,0,0.3)' }}>
                      <p className="font-oranienbaum text-white/40 text-xs mb-1">{item?.label}</p>
                      <p className="font-oranienbaum font-bold" style={{ color: item?.color, fontSize: 'var(--fs-lg)' }}>{item?.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Márgenes tab */}
      {activeTab === 'margenes' && (
        <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <h3 className="font-oranienbaum font-bold text-white mb-2" style={{ fontSize: 'var(--fs-section-title)' }}>Configuración Global de Márgenes</h3>
          <p className="font-oranienbaum text-white/40 mb-6" style={{ fontSize: 'var(--fs-sm)' }}>Pendiente de conexión con Supabase — Los márgenes se configurarán por producto, categoría y departamento.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {['Tienda Física', 'Tienda Online', 'Distribución']?.map(dept => (
              <div key={dept} className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <h4 className="font-oranienbaum font-bold text-white mb-3" style={{ fontSize: 'var(--fs-base)' }}>{dept}</h4>
                {['Mínimo', 'Objetivo', 'Premium']?.map(m => (
                  <div key={m} className="flex items-center justify-between py-2 border-b" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                    <span className="font-oranienbaum text-white/50" style={{ fontSize: 'var(--fs-sm)' }}>{m}</span>
                    <span className="font-oranienbaum text-white/30" style={{ fontSize: 'var(--fs-sm)' }}>—</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Lector tab */}
      {activeTab === 'lector' && <LectorPrecios />}
    </div>
  );
}

function LectorPrecios() {
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState('nombre');
  const [result, setResult] = useState(null);
  const [searching, setSearching] = useState(false);

  const handleSearch = () => {
    if (!query?.trim()) return;
    setSearching(true);
    setTimeout(() => {
      setResult(null); // Pendiente de conexión con Supabase
      setSearching(false);
    }, 800);
  };

  return (
    <div className="space-y-5">
      <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <h3 className="font-oranienbaum font-bold text-white mb-4" style={{ fontSize: 'var(--fs-section-title)' }}>🔍 Consultar Precio</h3>
        <div className="flex flex-wrap gap-2 mb-4">
          {[
            { id: 'ean', label: 'EAN' },
            { id: 'plu', label: 'PLU' },
            { id: 'codigo', label: 'Código Interno' },
            { id: 'nombre', label: 'Nombre' },
          ]?.map(t => (
            <button key={t?.id} onClick={() => setSearchType(t?.id)}
              className="px-3 py-1.5 rounded-lg font-oranienbaum transition-all"
              style={{
                background: searchType === t?.id ? 'rgba(214,239,78,0.15)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${searchType === t?.id ? 'rgba(214,239,78,0.35)' : 'rgba(255,255,255,0.08)'}`,
                color: searchType === t?.id ? '#D6EF4E' : 'rgba(255,255,255,0.55)',
                fontSize: 'var(--fs-sm)',
              }}>
              {t?.label}
            </button>
          ))}
        </div>
        <div className="flex gap-3">
          <div className="flex items-center gap-2 flex-1 rounded-xl px-4 border" style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.12)', minHeight: 'var(--control-height-md)' }}>
            <Icon name="Search" size={16} className="text-white/35" />
            <input value={query} onChange={e => setQuery(e?.target?.value)} onKeyDown={e => e?.key === 'Enter' && handleSearch()}
              placeholder={`Buscar por ${searchType}...`}
              className="flex-1 bg-transparent outline-none font-oranienbaum text-white placeholder-white/25"
              style={{ fontSize: 'var(--fs-base)' }} />
          </div>
          <button onClick={handleSearch} disabled={searching}
            className="flex items-center gap-2 px-5 rounded-xl font-oranienbaum font-bold transition-all"
            style={{ background: '#D6EF4E', color: '#000', fontSize: 'var(--fs-btn)', minHeight: 'var(--control-height-md)' }}>
            {searching ? <div className="w-4 h-4 rounded-full border-2 border-black/20 border-t-black animate-spin" /> : <Icon name="Search" size={16} />}
            Buscar
          </button>
        </div>
      </div>
      {/* Result */}
      {result === null && query && !searching && (
        <div className="rounded-2xl p-8 flex flex-col items-center gap-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <Icon name="Database" size={28} className="text-white/20" />
          <p className="font-oranienbaum font-bold text-white/50" style={{ fontSize: 'var(--fs-base)' }}>Pendiente de conexión con Supabase</p>
          <p className="font-oranienbaum text-white/30 text-center" style={{ fontSize: 'var(--fs-sm)' }}>El lector de precios mostrará coste real, PVP físico, PVP online, margen, stock, lote, caducidad, ubicación, última compra y última venta.</p>
        </div>
      )}
      {/* Quick actions placeholder */}
      <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <h4 className="font-oranienbaum font-bold text-white mb-3" style={{ fontSize: 'var(--fs-section-title)' }}>Acciones Rápidas</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {[
            { label: 'Editar precio', icon: 'Edit2' },
            { label: 'Recalcular precio', icon: 'RefreshCw' },
            { label: 'Imprimir etiqueta', icon: 'Printer' },
            { label: 'Enviar a báscula EPelsa', icon: 'Scale' },
            { label: 'Sincronizar tienda online', icon: 'Globe' },
            { label: 'Consultar historial', icon: 'History' },
          ]?.map((action, i) => (
            <button key={i} className="flex items-center gap-2 px-3 py-2.5 rounded-xl font-oranienbaum transition-all text-left"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.55)', fontSize: 'var(--fs-sm)' }}>
              <Icon name={action?.icon} size={14} className="text-white/35 flex-shrink-0" />
              {action?.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
